/**
 * 티켓 목록 페이지
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, Badge, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import DataTable, { Column } from '../../components/common/DataTable';
import Pagination from '../../components/common/Pagination';
import FilterBar, { FilterField } from '../../components/common/FilterBar';
import { ticketService } from '../../services/ticketService';
import { Ticket, TicketFilter, TicketStatus, TicketType } from '../../types/ticket';
import { format } from 'date-fns';

const TicketsList: React.FC = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<TicketFilter>({
    page: 1,
    limit: 20,
  });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['tickets', filter],
    queryFn: () => ticketService.getTickets(filter),
  });

  const getStatusBadge = (status: TicketStatus) => {
    const statusMap: Record<TicketStatus, { variant: string; label: string }> = {
      issued: { variant: 'success', label: '발행됨' },
      used: { variant: 'info', label: '사용됨' },
      expired: { variant: 'warning', label: '만료됨' },
      cancelled: { variant: 'danger', label: '취소됨' },
    };

    const statusInfo = statusMap[status] || { variant: 'secondary', label: status };
    return <Badge bg={statusInfo.variant}>{statusInfo.label}</Badge>;
  };

  const getTypeLabel = (type: TicketType) => {
    const typeMap: Record<TicketType, string> = {
      adult: '성인',
      youth: '청소년',
      disabled: '장애인',
      infant: '유아',
    };
    return typeMap[type] || type;
  };

  const columns: Column<Ticket>[] = [
    {
      key: 'ticket_number',
      label: '티켓번호',
      render: (ticket) => (
        <span style={{ fontWeight: 600, color: 'var(--color-accent-cyan)' }}>
          {ticket.ticket_number}
        </span>
      ),
    },
    {
      key: 'booking_number',
      label: '예약번호',
      render: (ticket) => ticket.booking?.booking_number || '-',
    },
    {
      key: 'ticket_type',
      label: '유형',
      render: (ticket) => getTypeLabel(ticket.ticket_type),
    },
    {
      key: 'product',
      label: '상품',
      render: (ticket) => ticket.hall?.name || ticket.package?.name || '-',
    },
    {
      key: 'visit_date',
      label: '방문일시',
      render: (ticket) => (
        <div>
          {ticket.booking?.visit_date ? format(new Date(ticket.booking.visit_date), 'yyyy-MM-dd') : '-'}
          {ticket.booking?.visit_time && (
            <small className="text-muted d-block">{ticket.booking.visit_time}</small>
          )}
        </div>
      ),
    },
    {
      key: 'entry_count',
      label: '입장 횟수',
      render: (ticket) => (
        <div>
          {ticket.remaining_entry_count} / {ticket.total_entry_count}
        </div>
      ),
    },
    {
      key: 'status',
      label: '상태',
      render: (ticket) => getStatusBadge(ticket.status),
    },
    {
      key: 'issued_at',
      label: '발행일시',
      render: (ticket) => format(new Date(ticket.issued_at), 'yyyy-MM-dd HH:mm'),
    },
    {
      key: 'last_entry',
      label: '최종 입장',
      render: (ticket) => ticket.last_entry_at 
        ? format(new Date(ticket.last_entry_at), 'yyyy-MM-dd HH:mm')
        : '-',
    },
  ];

  const filterFields: FilterField[] = [
    {
      name: 'status',
      label: '상태',
      type: 'select',
      options: [
        { value: 'issued', label: '발행됨' },
        { value: 'used', label: '사용됨' },
        { value: 'expired', label: '만료됨' },
        { value: 'cancelled', label: '취소됨' },
      ],
    },
    {
      name: 'ticket_type',
      label: '티켓 유형',
      type: 'select',
      options: [
        { value: 'adult', label: '성인' },
        { value: 'youth', label: '청소년' },
        { value: 'disabled', label: '장애인' },
        { value: 'infant', label: '유아' },
      ],
    },
    {
      name: 'issued_at',
      label: '발행일',
      type: 'dateRange',
    },
    {
      name: 'ticket_number',
      label: '티켓번호',
      type: 'text',
      placeholder: '티켓번호 검색',
    },
    {
      name: 'booking_number',
      label: '예약번호',
      type: 'text',
      placeholder: '예약번호 검색',
    },
  ];

  const handleFilterChange = (newFilter: Record<string, any>) => {
    setFilter({
      ...filter,
      ...newFilter,
      page: 1,
    });
  };

  const handlePageChange = (page: number) => {
    setFilter({ ...filter, page });
  };

  const handleRowClick = (ticket: Ticket) => {
    navigate(`/admin/tickets/${ticket.id}`);
  };

  return (
    <AdminLayout>
      <div>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h1 style={{ color: '#fff' }}>티켓 관리</h1>
        </div>

        <Card className="mb-4">
          <Card.Body>
            <FilterBar
              fields={filterFields}
              values={filter}
              onChange={handleFilterChange}
              onSearch={() => refetch()}
              onReset={() => setFilter({ page: 1, limit: 20 })}
            />
          </Card.Body>
        </Card>

        <Card>
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                총 <strong>{data?.pagination.total_count || 0}</strong>건
              </div>
            </div>

            <DataTable
              columns={columns}
              data={data?.tickets || []}
              loading={isLoading}
              emptyMessage="티켓 내역이 없습니다."
              onRowClick={handleRowClick}
            />

            {data && data.pagination.total_pages > 1 && (
              <Pagination
                currentPage={data.pagination.current_page}
                totalPages={data.pagination.total_pages}
                onPageChange={handlePageChange}
              />
            )}
          </Card.Body>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default TicketsList;

